import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import NativeMatchMap from "../lib/native_match_map"

export default class extends Controller {
  static values = {
    initialMarkers: { type: Array, default: [] }
  }

  connect() {
    this.markers = []
    this.markerData = []
    if (NativeMatchMap.available()) {
      this.nativeMap = new NativeMatchMap(this.element, () => this.fallbackToLeaflet())
    } else {
      this.initMap()
    }
    this.boundBeforeCache = () => this.disconnect()
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    document.addEventListener("turbo:before-render", this.boundBeforeCache)

    // Listen for match:added events from the room controller
    this.boundAddMarker = this.handleMatchAdded.bind(this)
    document.addEventListener("match:added", this.boundAddMarker)

    // Listen for invalidate events (when container becomes visible)
    this.boundInvalidate = () => {
      if (this.map) this.map.invalidateSize()
    }
    document.addEventListener("map:invalidate", this.boundInvalidate)

    // Load initial markers (for page refresh on completed rooms)
    this.loadingInitialMarkers = true
    this.initialMarkersValue.forEach(m => {
      this.addMarker(m.lat, m.lng, m.date, m.distance, m.matchId)
    })
    this.loadingInitialMarkers = false
    if (this.nativeMap) this.nativeMap.render(this.markerData)

    // Invalidate size after initial render in case container was hidden
    setTimeout(() => { if (this.map) this.map.invalidateSize() }, 200)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    document.removeEventListener("turbo:before-render", this.boundBeforeCache)
    document.removeEventListener("match:added", this.boundAddMarker)
    document.removeEventListener("map:invalidate", this.boundInvalidate)
    if (this.map) {
      this.map.remove()
      this.map = null
    }
    this.nativeMap?.destroy()
    this.nativeMap = null
  }

  fallbackToLeaflet() {
    this.nativeMap = null
    this.initMap()
    this.markerData.forEach(m => this.addLeafletMarker(m.lat, m.lng, m.date, m.distance))
  }

  initMap() {
    this.map = L.map(this.element).setView([20, 0], 2)

    // Use LocationIQ tiles when a key is configured (production — OSM's free tile
    // servers ban heavy/commercial use, same as Nominatim did); otherwise fall back
    // to the free OSM tiles for dev / before the key is set.
    const tileKey = document.querySelector('meta[name="locationiq-tile-key"]')?.content
    if (tileKey) {
      L.tileLayer(`https://{s}-tiles.locationiq.com/v3/streets/r/{z}/{x}/{y}.png?key=${tileKey}`, {
        attribution: '&copy; <a href="https://locationiq.com/">LocationIQ</a> &amp; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
        subdomains: "abc"
      }).addTo(this.map)
    } else {
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(this.map)
    }

    // Fix Leaflet icon paths (broken with bundlers)
    delete L.Icon.Default.prototype._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
      iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
      shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png"
    })
  }

  handleMatchAdded(event) {
    const { lat, lng, date, distance, matchId } = event.detail
    this.addMarker(lat, lng, date, distance, matchId)
  }

  addMarker(lat, lng, date, distance, matchId) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return
    this.markerData.push({ lat, lng, date, distance, matchId })
    if (this.nativeMap) {
      if (!this.loadingInitialMarkers) this.nativeMap.render(this.markerData)
    } else {
      this.addLeafletMarker(lat, lng, date, distance)
    }
  }

  addLeafletMarker(lat, lng, date, distance) {
    if (!this.map) return

    const dateStr = new Date(date + "T00:00:00").toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric"
    })

    const marker = L.marker([lat, lng]).addTo(this.map)
    marker.bindPopup(`<strong>${dateStr}</strong><br>${Math.round(distance)}m apart`)
    this.markers.push(marker)

    // Auto-fit bounds
    if (this.markers.length === 1) {
      this.map.setView([lat, lng], 13)
    } else {
      const group = L.featureGroup(this.markers)
      this.map.fitBounds(group.getBounds().pad(0.1))
    }
  }
}
